/**
 * Riga della vista "Tutte" del drawer Messaggi, con il menu (tasto destro o "...")
 * per fissare in alto, archiviare e silenziare le conversazioni WhatsApp, Instagram e
 * Messenger. Le chat di team non hanno queste voci.
 */
import React, { useState } from 'react';
import { MoreHorizontal, Pin, BellOff, MessageCircle, Instagram, Facebook, Users } from 'lucide-react';
import type { UnifiedChatItem, UnifiedChannel } from '@/lib/utils/chatListMerging';
import { TimeAgo } from '@/components/whatsapp2/shared/TimeAgo';
import { ChatRowMenu } from '@/components/whatsapp2/sidebar/ChatRowMenu';
import type { MuteOption } from '@/lib/services/whatsapp/chatListRules';
import { tip } from './ChatTooltip';
import { ConversationAvatar } from './ConversationAvatar';

export const CHANNEL_META: Record<UnifiedChannel, { label: string; icon: React.ComponentType<{ className?: string }>; chip: string }> = {
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  instagram: { label: 'Instagram', icon: Instagram, chip: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300' },
  messenger: { label: 'Messenger', icon: Facebook, chip: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  team: { label: 'Team', icon: Users, chip: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
};

interface Props {
  item: UnifiedChatItem;
  active: boolean;
  whatsappReady: boolean;
  onSelect: (item: UnifiedChatItem) => void;
  /** Stato fissa/archivia/silenzia; null per le chat di team. */
  prefs: { pinned: boolean; archived: boolean; muted: boolean } | null;
  prefsAvailable: boolean;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  onMute: (option: MuteOption | null) => void;
}

export function AllInboxRow({ item, active, whatsappReady, onSelect, prefs, prefsAvailable, onTogglePin, onToggleArchive, onMute }: Props) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const meta = CHANNEL_META[item.channel];
  const Icon = meta.icon;
  const unread = item.unreadCount > 0;
  const muted = !!prefs?.muted;
  const badge = muted ? 'bg-[#9B9A97]' : 'bg-[#2383e2]';

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => onSelect(item)}
        onContextMenu={prefs ? (e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY }); } : undefined}
        className={[
          'w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 transition-colors',
          active ? 'bg-[rgba(35,131,226,0.08)]' : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5',
        ].join(' ')}
      >
        <div className="relative flex-shrink-0">
          <ConversationAvatar
            name={item.name}
            imageUrl={item.avatarUrl}
            isGroup={item.isGroup}
            whatsappChatId={item.source === 'whatsapp' && whatsappReady ? item.id : undefined}
          />
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white dark:bg-[#191919] flex items-center justify-center" aria-hidden>
            <Icon className="h-3 w-3 text-[#787774]" />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${unread ? 'font-semibold text-[#37352F] dark:text-white' : 'font-medium text-[#37352F] dark:text-gray-200'}`}>
              {item.name}
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {muted && <BellOff className="w-3 h-3 text-[#9B9A97]" aria-label="Silenziata" />}
              {prefs?.pinned && <Pin className="w-3 h-3 text-[#9B9A97]" aria-label="Fissata in alto" />}
              <TimeAgo timestamp={item.timestamp || null} hasUnread={unread && !muted} />
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${meta.chip}`}>
              {meta.label}
            </span>
            <span className={`text-xs truncate ${unread ? 'text-[#37352F] dark:text-gray-200' : 'text-[#787774]'}`}>{item.snippet}</span>
            {unread && item.channel !== 'instagram' && item.channel !== 'messenger' && (
              <span className={`ml-auto min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full ${badge} text-white text-[10px] font-semibold flex-shrink-0`}>
                {item.unreadCount > 99 ? '99+' : item.unreadCount}
              </span>
            )}
            {unread && (item.channel === 'instagram' || item.channel === 'messenger') && (
              <span className={`ml-auto h-2 w-2 rounded-full ${badge} flex-shrink-0`} aria-label="Da rispondere" />
            )}
          </div>
        </div>
      </button>

      {prefs && (
        <button
          type="button"
          aria-label="Altre azioni sulla conversazione"
          {...tip('Fissa, archivia, silenzia')}
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right - 210, y: r.bottom + 4 }); }}
          className={`absolute right-2 bottom-2 p-1 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-[rgba(55,53,47,0.09)] dark:border-white/10 text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-opacity ${menu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      )}

      {menu && prefs && (
        <ChatRowMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          pinned={prefs.pinned}
          archived={prefs.archived}
          muted={prefs.muted}
          prefsAvailable={prefsAvailable}
          onTogglePin={onTogglePin}
          onToggleArchive={onToggleArchive}
          onMute={onMute}
        />
      )}
    </div>
  );
}
